import { useUrlSearchParamFlagControls } from '@/hooks'
import { EnergySystemDemandResponseEvent } from '@/types/energySystemDemandResponseEvent'
import { getDateFromTimestamp } from '@/utils/timeUtilities'
import { match } from 'ts-pattern'

export function getUpcomingEventTimeText(
  event: EnergySystemDemandResponseEvent,
) {
  /**
   * example: You have an upcoming event from 7:00 PM to 10:00 PM.
   */
  const formattedStartTime = getDateFromTimestamp(
    event.start_inclusive,
    'h:mm A',
  )
  const formattedEndTime = getDateFromTimestamp(event.end_exclusive, 'h:mm A')

  return `You have an upcoming event from ${formattedStartTime} to ${formattedEndTime}.`
}

export function getEventSettingsText(event: EnergySystemDemandResponseEvent) {
  const settingsWillChange = event.setting_changes.some(
    (setting) =>
      setting.current_customer_preferred_setting != setting.dispatched_setting,
  )
  if (settingsWillChange) {
    return 'Optiwatt may adjust your battery settings and will revert them once the event ends.'
  }
  return ''
}

export function getUpcomingEventText(event: EnergySystemDemandResponseEvent) {
  return `${getUpcomingEventTimeText(event)} ${getEventSettingsText(event)}`
}

export function getActiveEventTimeText(event: EnergySystemDemandResponseEvent) {
  const formattedStartTime = getDateFromTimestamp(
    event.start_inclusive,
    'h:mm A',
  )
  const formattedEndTime = getDateFromTimestamp(event.end_exclusive, 'h:mm A')
  if (event.enrolled) {
    return `You’re currently participating in an event from ${formattedStartTime} to ${formattedEndTime}.`
  }
  return `There’s currently an event from ${formattedStartTime} to ${formattedEndTime}.`
}

export function getActiveEventText(event: EnergySystemDemandResponseEvent) {
  return `${getActiveEventTimeText(event)} ${getEventSettingsText(event)}`
}

export function useDRAlertModalLink(args: {
  event: EnergySystemDemandResponseEvent
  outParam: string
  inParam: string
}) {
  const { event, outParam, inParam } = args
  const {
    isOn: optOutModalOpen,
    onLocation: optOutModalOnLocation,
    setFlag: setOptOutModalFlag,
  } = useUrlSearchParamFlagControls(outParam)
  const {
    isOn: optInStatusModalOpen,
    onLocation: optInStatusModalOnLocation,
    setFlag: setOptInStatusModalFlag,
  } = useUrlSearchParamFlagControls(inParam)

  const { linkTo, linkText } = match(event.enrolled)
    .with(true, () => ({ linkTo: optOutModalOnLocation, linkText: 'Manage' }))
    .with(false, () => ({
      linkTo: optInStatusModalOnLocation,
      linkText: 'Opt In',
    }))
    .exhaustive()

  return {
    linkTo,
    linkText,
    optOutModalOpen,
    onOptOutModalClose: () => setOptOutModalFlag(false),
    onOptInStatusModalClose: () => setOptInStatusModalFlag(false),
    optInStatusModalOpen,
  }
}
